import { NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../lib/auth.tsx";
import { useTheme } from "../lib/theme.tsx";
import { KryptonBrand, OizomEndorsement } from "./ui/Logo.tsx";

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  icon: ReactNode;
}

const ICON = {
  overview: (
    <>
      <rect x="3" y="3" width="7" height="8" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="15" width="7" height="6" rx="1.5" />
    </>
  ),
  devices: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
    </>
  ),
  live: (
    <>
      <circle cx="12" cy="12" r="1.8" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M6 6a9 9 0 0 0 0 12M18 6a9 9 0 0 1 0 12" />
    </>
  ),
  firmware: (
    <>
      <path d="M12 3v11M8 10l4 4 4-4" />
      <path d="M5 20h14" />
    </>
  ),
  templates: (
    <>
      <path d="M12 3l9 5-9 5-9-5z" />
      <path d="M3 13l9 5 9-5" />
    </>
  ),
  parameters: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.3 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>
  ),
} as const;

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Monitor",
    items: [
      { to: "/overview", label: "Overview", icon: ICON.overview },
      { to: "/", label: "Devices", end: true, icon: ICON.devices },
      { to: "/live", label: "Live monitor", icon: ICON.live },
    ],
  },
  {
    group: "Manage",
    items: [
      { to: "/firmware", label: "Firmware", icon: ICON.firmware },
      { to: "/templates", label: "Config templates", icon: ICON.templates },
      { to: "/parameters", label: "Parameters", icon: ICON.parameters },
    ],
  },
  {
    group: "Access",
    items: [{ to: "/settings", label: "Settings", icon: ICON.settings }],
  },
];

function NavIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[17px] w-[17px] flex-none"
    >
      {children}
    </svg>
  );
}

const SunIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" className="h-3.5 w-3.5">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
  </svg>
);
const MoonIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
    <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.6 6.6 0 0 0 9.8 9.8Z" />
  </svg>
);

// Shared chrome for every protected page — the Envizom-style left sidebar
// (grouped modules, Krypton brand, Oizom endorsement, theme + sign out). Pages
// render their own content into the main slot. The sidebar is always dark.
export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const initials = (user?.email ?? "?").slice(0, 2).toUpperCase();

  return (
    <div className="flex min-h-screen bg-page">
      <aside className="sticky top-0 flex h-screen w-[236px] flex-none flex-col bg-side text-sidetext">
        <div className="px-[18px] pb-3.5 pt-4">
          <KryptonBrand subtitle="oizom web" />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {NAV.map(({ group, items }) => (
            <div key={group}>
              <div className="px-2.5 pb-1.5 pt-3.5 text-[9.5px] font-bold uppercase tracking-[0.15em] text-sidemut">
                {group}
              </div>
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  className={({ isActive }) =>
                    "flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-[13px] font-medium transition " +
                    (isActive
                      ? "bg-fill font-semibold text-white shadow-[0_4px_12px_-4px_rgba(64,173,72,0.7)]"
                      : "text-sidetext/90 hover:bg-white/[0.06]")
                  }
                >
                  <NavIcon>{it.icon}</NavIcon>
                  {it.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/[0.08] p-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-fill text-[11px] font-bold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[12px] font-semibold text-white">{user?.email}</div>
              <div className="text-[10.5px] capitalize text-sidemut">Administrator</div>
            </div>
          </div>
          <div className="mt-2.5 flex gap-1.5">
            <button
              onClick={toggle}
              title="Toggle day / night"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/[0.14] bg-white/[0.04] py-1.5 text-[11px] text-sidetext hover:bg-white/10"
            >
              {theme === "night" ? SunIcon : MoonIcon}
            </button>
            <button
              onClick={logout}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/[0.14] bg-white/[0.04] py-1.5 text-[11px] text-sidetext hover:bg-white/10"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-3.5 w-3.5">
                <path d="M16 17l5-5-5-5M21 12H9M12 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6" />
              </svg>
              Sign out
            </button>
          </div>
          <OizomEndorsement className="mt-3 border-t border-white/[0.08] pt-3" />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
}
